import { useCubeQuery } from "../hooks/useCubeQuery";
import useCubeClient from "../cubejs-client";
import { BinaryOperator } from "@cubejs-client/core";
import { CampaignTargetType, CampaignType } from "../../__generated__/graphql";

interface CampaignItem {
  _id: string;
  name?: string;
  marketplace: string;
  type: CampaignType;
  targetValue?: number;
  targetType: CampaignTargetType;
  startDate?: string;
  endDate?: string;
  status?: string;
  advertiser?: { name?: string };
  publishers?: { name: string }[];
  ownerCompany?: { name?: string };
  ownerCompanyId: string;
  publisherIds?: string[];
  publications?: string[];
  labels?: {name: string}[];
}

interface CostItem {
  "aggregateTdLevel.campaignId": string;
  "aggregateTdLevel.totalCost": string;
  "aggregateTdLevel.reads": number;
  "aggregateTdLevel.clickouts": number;
  "aggregateTdLevel.tdImpressions": number;
  "aggregateTdLevel.tdClicks": number;
}

export const useConditionalCubeQuery = (campaignIds: string[]) => {
  const cubejsApi = useCubeClient()

  const query = {
    measures: [
      "aggregateTdLevel.totalCost",
      "aggregateTdLevel.tdClicks",
      "aggregateTdLevel.reads",
      "aggregateTdLevel.tdImpressions",
      "aggregateTdLevel.clickouts",
      "aggregateTdLevel.conversions",
      "aggregateTdLevel.publisherRevenue",
    ],
    dimensions: ["aggregateTdLevel.campaignId"],
    filters: [
      {
        member: "aggregateTdLevel.campaignId",
        operator: "in" as BinaryOperator,
        values: campaignIds,
      },
      {
        member: "aggregateTdLevel.fraud",
        operator: "notEquals" as BinaryOperator,
        values: ["true"],
      },
    ],
  };

  //@ts-ignore
  return useCubeQuery(query, { cubejsApi, skip: campaignIds.length === 0 });
};

const getCampaignIds = (campaigns: Array<{ _id: string }>): string[] => {
  return campaigns.map((campaign) => campaign._id);
};

export const useCampaignsWithCost = (campaigns: any[] = []) => {
  const campaignIds = getCampaignIds(campaigns);
  const costArray = useConditionalCubeQuery(campaignIds);
  let results = (costArray as any).resultSet?.loadResponse?.results;
  let costObj: { [key: string]: CostItem } = {};

  if (results && results.length > 0) {
    costObj = results[0].data.reduce(
      (acc: { [key: string]: CostItem }, item: CostItem) => {
        acc[item["aggregateTdLevel.campaignId"]] = item;
        return acc;
      },
      {}
    );
  }
  let campaignObj: { [key: string]: CampaignItem } = {};

  if (campaigns) {
    campaignObj = campaigns.reduce(
      (acc: { [key: string]: CampaignItem }, item: CampaignItem) => {
        acc[item._id] = item;
        return acc;
      },
      {}
    );
  }

  const merged = Object.keys(campaignObj).map((_id: string) => {
    const costItem = costObj[_id] || {};
    const campaignItem = campaignObj[_id] || {};
    return {
      _id,
      name: campaignItem.name || "",
      type: campaignItem.type,
      marketplace: campaignItem.marketplace,
      targetValue: campaignItem.targetValue || 0,
      targetType: campaignItem.targetType,
      labels: campaignItem.labels,
      cost: parseInt(costItem["aggregateTdLevel.totalCost"], 10) || 0,
      reads: costItem["aggregateTdLevel.reads"] || 0,
      clicks: costItem["aggregateTdLevel.clickouts"] || 0,
      impressions: costItem["aggregateTdLevel.tdImpressions"] || 0,
      revenue: costItem['aggregateTdLevel.publisherRevenue'] || 0,
      conversions: costItem['aggregateTdLevel.conversions'] || 0,
      tdClicks: costItem['aggregateTdLevel.tdClicks'] || 0,
      startDate: campaignItem.startDate || "",
      endDate: campaignItem.endDate || "",
      status: campaignItem.status || "",
      advertiser: campaignItem.advertiser?.name || "",
      publisherIds: campaignItem.publisherIds || [],
      publications: campaignItem.publications || [],
      publishers: campaignItem.publishers || [],
      owner: campaignItem.ownerCompany?.name || "",
      ownerCompanyId: campaignItem.ownerCompanyId || "",
    };
  });

  return merged;
};
